import { useFetch } from "@/composables/useFetch";
import { useState } from "@/composables/useState";
import type { User } from "@/types/api";
import { router } from "@/router";
import Keycloak, { type KeycloakInitOptions } from "keycloak-js";
import { acceptHMRUpdate, defineStore } from "pinia";
import { computed, type App } from "vue";

// Keycloak Options
const KEYCLOAK_INIT_OPTIONS: KeycloakInitOptions = {
  onLoad: "login-required",
  redirectUri: window.location.origin,
  checkLoginIframe: false,
  enableLogging: true,
  pkceMethod: "S256",
  token: localStorage.getItem("kc_token")!,
  refreshToken: localStorage.getItem("kc_refresh_token")!,
};

export const useAuth = defineStore("auth", () => {
  const [keycloak, setKeycloak] = useState<Keycloak | null>(null);
  const [user, setUser] = useState<User | null>(null);

  const isUserAdmin = computed(() => user.value?.is_admin ?? false);

  function initializeKeycloak(app: App) {
    keycloak.value
      ?.init(KEYCLOAK_INIT_OPTIONS)
      .then(async (authenticated) => {
        if (!authenticated) {
          keycloak.value?.login();
        } else {
          // remove keycloak-related parameters in the URL hash
          if (window.location.hash.includes("state=")) {
            history.replaceState(null, "", window.location.pathname);
          }

          fetchUser().then(() => {
            app.mount("#app");
            if (router.currentRoute.value.name === "503") router.push({ name: "home" });
          });
        }
        setInterval(() => {
          updateToken(70);
        }, 6000);
      })
      .catch((error) => {
        console.error(`Failed to initialize Keycloak: ${error?.message}`);
        app.mount("#app");
        router.push({ name: "503" });
      });
  }

  async function fetchUser() {
    try {
      const data = await useFetch<User>({ method: "GET", url: "/me" });
      setUser(data);
      if (keycloak.value?.token) {
        localStorage.setItem("kc_token", keycloak.value?.token);
      }
      if (keycloak.value?.refreshToken) {
        localStorage.setItem("kc_refresh_token", keycloak.value?.refreshToken);
      }
    } catch (error) {
      console.error(error);
    }
  }

  async function logout() {
    keycloak.value!.logout();
  }

  function updateToken(minValiditySeconds: number) {
    keycloak
      .value!.updateToken(minValiditySeconds)
      .then(async (refreshed) => {
        if (refreshed) await fetchUser();
      })
      .catch((err) => {
        console.error(`Failed to refresh token: ${err}`);
      });
  }

  return {
    user,
    keycloak,
    isUserAdmin,
    setKeycloak,
    fetchUser,
    logout,
    updateToken,
    initializeKeycloak,
  };
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuth, import.meta.hot));
}
